/**
 * serviceCatalog.js — Service Database (SVC_DB)
 *
 * Complete product catalog for Roto Flight's video/photo services.
 * Ported directly from v1 quotes.js SVC_DB constant.
 *
 * Pricing models:
 * - Tiered: price based on property square footage (10 tiers)
 * - Flat: fixed price regardless of size
 * - Film bands: price based on property value
 *
 * Tier index mapping (sq ft):
 *   0: 0–1000, 1: 1001–1500, 2: 1501–2000, 3: 2001–2500,
 *   4: 2501–3000, 5: 3001–4000, 6: 4001–5000, 7: 5001–7500,
 *   8: 7501–10000, 9: 10001+
 */

export const SVC_DB = {
  categories: [
    {
      id: 'photo', num: 1, name: 'Photography', color: '#06B3E8', icon: '📷',
      desc: 'MLS-ready professional marketing images. AB 723-compliant editing.',
      products: [
        { id: 'photo_ie', name: 'Interior & Exterior', desc: 'Interior + exterior photo coverage',
          tiers: [349, 399, 449, 499, 549, 599, 649, 699, 749, 799], dur: [45, 60, 75, 90, 105, 120, 135, 155, 175, 195] },
        { id: 'photo_iea', name: 'Interior & Exterior + Aerial', desc: 'Full coverage with drone aerials',
          tiers: [499, 549, 599, 649, 699, 749, 799, 849, 899, 949], dur: [60, 75, 90, 105, 120, 135, 150, 165, 180, 195] },
        { id: 'photo_tw', name: 'Twilight Only', desc: 'Dedicated twilight exterior session', flat: 299, dur: 60 },
        { id: 'photo_twa', name: 'Twilight + Aerial', desc: 'Twilight shoot with drone aerials', flat: 349, dur: 60 },
      ],
    },
    {
      id: 'drone', num: 2, name: 'Indoor Drone / FPV Tour', color: '#2E7D9B', icon: '🚁',
      desc: 'One-take FPV drone flight through the home in a single continuous motion.',
      products: [
        { id: 'drone_mls', name: 'FPV — MLS Video', desc: 'Horizontal format for MLS',
          tiers: [649, 749, 799, 849, 899, 1099, 1199, 1299, 1399, 1499], dur: [45, 50, 55, 60, 65, 70, 75, 80, 85, 90] },
        { id: 'drone_reel', name: 'FPV — Social Reel', desc: 'Vertical format for social',
          tiers: [649, 749, 799, 849, 899, 1099, 1199, 1299, 1399, 1499], dur: [45, 50, 55, 60, 65, 70, 75, 80, 85, 90] },
        { id: 'drone_bundle', name: 'FPV — MLS + Reel Bundle', desc: 'Both formats ~25% off', note: 'SAVE ~25%',
          tiers: [999, 1129, 1199, 1279, 1349, 1649, 1799, 1949, 2099, 2249], dur: [60, 50, 55, 60, 65, 70, 75, 80, 85, 90] },
      ],
    },
    {
      id: 'video', num: 3, name: 'Standard Highlight Video', color: '#C94A2A', icon: '🎬',
      desc: 'Cinematic ground + aerial video. Music-driven edit, MLS-ready delivery.',
      products: [
        { id: 'video_mls', name: 'Property Video — MLS', desc: 'Up to 1-min cinematic video',
          tiers: [649, 699, 749, 799, 849, 899, 949, 999, 1049, 1099], dur: [60, 75, 90, 105, 120, 135, 140, 155, 170, 185] },
        { id: 'video_reel', name: 'Property Video — Social Reel', desc: '30-60s vertical social edit',
          tiers: [649, 699, 749, 799, 849, 899, 949, 999, 1049, 1099], dur: [60, 75, 90, 105, 120, 135, 150, 165, 180, 195] },
        { id: 'video_combo', name: 'Property Video — MLS + Reel', desc: 'Both formats combined',
          tiers: [1299, 1399, 1499, 1599, 1699, 1799, 1899, 1999, 2099, 2199], dur: [90, 105, 120, 135, 150, 165, 180, 195, 210, 225] },
      ],
    },
    {
      id: 'floor', num: 4, name: 'Floor Plan \u00b7 Matterport \u00b7 Zillow', color: '#7B5EA7', icon: '\ud83d\uddfa',
      desc: 'Layout visuals, immersive 3D tours, and premium Zillow listings.',
      products: [
        { id: 'fp_2d', name: '2D Floor Plan', desc: 'Clean schematic layout',
          wideBands: true, tiers: [50, 50, 74, 74, 99, 99, 99, 149, 149, 149], dur: [30, 30, 35, 35, 40, 40, 40, 45, 45, 45] },
        { id: 'fp_2d3d', name: '2D + 3D Floor Plan', desc: 'Traditional 2D + 3D visual',
          wideBands: true, tiers: [149, 149, 174, 174, 249, 249, 249, 349, 349, 349], dur: [30, 30, 35, 35, 40, 40, 40, 45, 45, 45] },
        { id: 'matterport', name: 'Matterport Tour', desc: 'Interactive 3D walk-through',
          tiers: [399, 449, 499, 549, 599, 649, 699, 749, 799, 849], dur: [60, 60, 65, 70, 75, 80, 85, 90, 95, 100] },
        { id: 'matterport_fp', name: 'Matterport + Floor Plan', desc: '3D tour + detailed floor plan',
          tiers: [535, 635, 735, 835, 935, 1035, 1135, 1235, 1335, 1435], dur: [60, 60, 65, 70, 75, 80, 85, 90, 95, 100] },
        { id: 'zillow', name: 'Zillow Showcase w/ Floor Plan', desc: 'Premium Zillow listing experience',
          tiers: [249, 299, 349, 399, 449, 499, 549, 599, 649, 699], dur: [30, 45, 60, 70, 80, 90, 100, 110, 120, 130] },
      ],
    },
    {
      id: 'film', num: 5, name: 'Property Film', color: '#B8860B', icon: '\ud83c\udf9e',
      desc: 'Fully custom multi-day cinematic production. Bespoke creative concept.',
      products: [
        { id: 'film_sig', name: 'Signature Luxury Production', desc: 'Multi-day cinematic property film',
          filmBands: true, filmTiers: [
            { max: 1499, price: 3000, dur: 240 },
            { max: 2999, price: 4500, dur: 360 },
            { max: 4499, price: 6000, dur: 360 },
            { max: 5999, price: 7500, dur: 360 },
            { max: 10000, price: 9500, dur: 360 },
          ] },
      ],
    },
    {
      id: 'aerial', num: 6, name: 'Aerial Photo / Video Only', color: '#2E8B57', icon: '\u2708\ufe0f',
      desc: 'Drone visuals for property context. All products flat-rate \u2014 no sq ft needed.',
      products: [
        { id: 'aerial_home', name: 'Aerial Home Photography', desc: '5-10 aerial stills, MLS-ready', flat: 299, dur: 30 },
        { id: 'aerial_lot_photo', name: 'Vacant Lot Photography', desc: 'Lot boundaries and terrain', flat: 299, dur: 30 },
        { id: 'aerial_lot_video', name: 'Vacant Lot Video (1 min)', desc: 'Cinematic aerial lot video', flat: 499, dur: 30 },
        { id: 'aerial_lot_bundle', name: 'Vacant Lot Photo + Video', desc: 'Complete lot package', flat: 699, dur: 60, note: 'SAVE $99' },
      ],
    },
  ],

  addons: [
    { id: 'ao_beacons', name: 'Location Beacons', desc: 'Map markers on aerial images', applies: ['photo', 'aerial'], excludeProducts: ['photo_ie', 'photo_tw'], perImage: true, price: 45 },
    { id: 'ao_proplines', name: 'Property Lines Overlay', desc: 'Lot boundaries on aerials', applies: ['photo', 'aerial'], excludeProducts: ['photo_ie', 'photo_tw'], perImage: true, price: 35 },
    { id: 'ao_vtwilight', name: 'Virtual Twilight', desc: 'Daytime \u2192 dusk transformation (per image)', applies: ['photo'], perImage: true, price: 25 },
    { id: 'ao_branded_new', name: 'Branded Video \u2014 New Client', desc: 'Agent end card creation', applies: ['drone', 'video', 'aerial'], price: 35 },
    { id: 'ao_branded_ret', name: 'Branded Video \u2014 Returning', desc: 'End card on file \u2014 included', applies: ['drone', 'video', 'aerial'], price: 0 },
    { id: 'ao_3d_single', name: '3D Graphics / Roto Tour \u2014 Single', desc: 'Floating 3D graphics (one format)', applies: ['drone'], price: 249 },
    { id: 'ao_3d_both', name: '3D Graphics / Roto Tour \u2014 Both', desc: '3D graphics V+H formats', applies: ['drone'], price: 375 },
    { id: 'ao_d2n_single', name: 'Day to Night \u2014 Single Format', desc: 'Day-to-night transition', applies: ['drone'], price: 800, durAdd: 60 },
    { id: 'ao_d2n_both', name: 'Day to Night \u2014 Both Formats', desc: 'Day-to-night MLS + Social', applies: ['drone'], price: 1200, durAdd: 120 },
    { id: 'ao_timelapse', name: 'Timelapse Shots', desc: 'Light & atmosphere sequences', applies: ['video'], price: 199 },
    { id: 'ao_vidlength', name: 'Video Length Upgrade (2 min+)', desc: 'Extended MLS video', applies: ['video'], price: 299, durAdd: 30 },
    { id: 'ao_luxury', name: 'Luxury Upgrade + Video Length', desc: 'Full cinematic upgrade with twilight, FPV, enhanced edit', applies: ['video'], price: 699, durAdd: 60 },
  ],
};

/**
 * Get price and duration for a product at a given square footage.
 * Handles tiered, flat, and film-band pricing models.
 */
export function svcGetPrice(product, sqft = 0) {
  if (product.flat !== undefined) {
    return { price: product.flat, dur: product.dur };
  }
  if (product.filmBands) {
    for (const band of product.filmTiers) {
      if (sqft <= band.max) return { price: band.price, dur: band.dur };
    }
    const last = product.filmTiers[product.filmTiers.length - 1];
    return { price: last.price, dur: last.dur };
  }
  // Standard tiered pricing by sq ft
  const idx = Math.min(Math.max(0, Math.floor(sqft / 1000)), 9);
  return { price: product.tiers[idx], dur: Array.isArray(product.dur) ? product.dur[idx] : product.dur };
}

/**
 * Find a product by its ID across all categories.
 * Returns { category, product } or null.
 */
export function findProduct(productId) {
  for (const cat of SVC_DB.categories) {
    const product = cat.products.find((p) => p.id === productId);
    if (product) return { category: cat, product };
  }
  return null;
}

/**
 * Get all add-ons applicable to a set of selected category IDs,
 * filtering out add-ons whose excludeProducts covers all selected products.
 */
export function getApplicableAddons(selectedCategoryIds, selectedProductIds = []) {
  const catSet = new Set(selectedCategoryIds);
  const prodSet = new Set(selectedProductIds);
  return SVC_DB.addons.filter((addon) => {
    if (!addon.applies.some((catId) => catSet.has(catId))) return false;
    if (addon.excludeProducts?.length && prodSet.size > 0) {
      // Check if at least one selected product is NOT excluded
      const relevantProducts = selectedProductIds.filter((pid) => {
        const found = findProduct(pid);
        return found && addon.applies.includes(found.category.id);
      });
      if (relevantProducts.length > 0 && relevantProducts.every((pid) => addon.excludeProducts.includes(pid))) {
        return false;
      }
    }
    return true;
  });
}

/**
 * Sq ft tier labels for display.
 */
export const SQ_FT_TIERS = [
  '0 – 1,000', '1,001 – 1,500', '1,501 – 2,000', '2,001 – 2,500',
  '2,501 – 3,000', '3,001 – 4,000', '4,001 – 5,000', '5,001 – 7,500',
  '7,501 – 10,000', '10,001+',
];

/*
 * ── TWO PRICE SETS ──────────────────────────────────────────────────────────
 * The website moved Real Estate to new prices and 500 sq ft size bands on
 * 2026-09-30, so the quote builder carries both lists, as it does for VirTour:
 *
 * - `legacy` ("Old") — SVC_DB above, exactly as it was (last changed Apr 2026),
 *   priced by svcGetPrice on 1,000 sq ft steps.
 * - `current` ("New") — SVC_DB_NEW below: the website's prices and ladders as of
 *   2026-09-30 (rotoflight-media src/data/serviceCatalogV2.js + addOnsV2.js).
 *
 * The version is chosen PER QUOTE (`state.rePriceVersion`, the price-list tab in
 * the picker) and stamped on every RE line as `priceVersion`. A quote with no
 * recorded version was made before this file had a new list, so it is legacy by
 * definition. Read prices through the rg* accessors, never off the fields.
 *
 * Ids are shared where a RotoGears item maps to a website one (photo_ie is the
 * Ambient Photos tier, ao_beacons became Landmark callouts), so a quote moved
 * between lists keeps its picks. Every Old id also exists in New: where the
 * website sells no counterpart, New carries the Old item unchanged. Items the
 * website added exist only in New.
 */

/** The two price sets, newest first. `id` is what a quote stores. */
export const RG_PRICE_VERSIONS = [
  { id: 'current', label: 'New', sub: 'Sep 30, 2026', asOf: '2026-09-30' },
  { id: 'legacy', label: 'Old', sub: 'Apr 2026', asOf: '2026-04-06' },
];

export const RG_CURRENT_VERSION = 'current';
export const RG_LEGACY_VERSION = 'legacy';

/** Anything that isn't an explicit 'current' reads as legacy (see above). */
export function rgNormalizeVersion(version) {
  return version === RG_CURRENT_VERSION ? RG_CURRENT_VERSION : RG_LEGACY_VERSION;
}

/*
 * New-list sizes: one price per 1,000 sq ft tier (0: 0–999 … 10: 10,000+), and
 * from 1,000 to 9,999 each tier splits into two 500 sq ft bands. The lower half
 * keeps the tier's price; the upper half pays the midpoint to the next tier,
 * rounded up to end in 9 ($399 and $449 give $429), the website's stepPrice.
 * `wholeSteps` lists stay on 1,000 sq ft steps (the 2D Floor Plan, priced from
 * CubiCasa's own bands, and the Simple Reel).
 */
export function rgTierIndex(sqft = 0) {
  const n = Math.floor(Number(sqft) || 0);
  if (n <= 0) return 0;
  return Math.min(Math.floor(n / 1000), 10);
}

function rgUpperHalf(sqft) {
  const n = Math.floor(Number(sqft) || 0);
  const t = rgTierIndex(n);
  return t >= 1 && t <= 9 && n % 1000 >= 500;
}

/** A 1,000 sq ft price list at a size. null entries are quoted, never priced. */
export function rgStepPrice(prices, tierIndex, up = false, wholeSteps = false) {
  const at = (i) => prices[Math.min(i, prices.length - 1)];
  const lo = at(tierIndex);
  if (lo == null) return null;
  if (!up || wholeSteps || tierIndex >= prices.length - 1) return lo;
  const hi = at(tierIndex + 1);
  if (!(hi > lo)) return lo;
  return Math.ceil((lo + hi) / 2 / 10) * 10 - 1;
}

// Website ladders, one entry per tier. The reel shelf is built from the Listing
// Video ladder as the site builds it: Premium = LV - $200, Dynamic = Premium +
// the $100 Speed ramps option, Story Reel = LV + $300.
const NEW_LV = [649, 699, 749, 849, 949, 1049, 1149, 1249, 1349, 1449, 1699];
const NEW_PREMIUM_REEL = NEW_LV.map((p) => p - 200);
const NEW_REEL_SPEED_RAMPS = 100;
const NEW_AMBIENT = [349, 399, 449, 499, 549, 599, 649, 699, 749, 799, 999];
const NEW_FLASH = [749, 849, 949, 1049, 1149, 1249, 1349, 1449, 1549, 1649, 1749];
const NEW_PACED_TOUR = [799, 849, 899, 999, 1099, 1199, 1299, 1399, 1499, 1599, 1849];
const NEW_STORY_TOUR = [1199, 1249, 1299, 1399, 1499, 1599, 1699, 1799, 1899, 1999, 2249];
const NEW_FP2D = [50, 50, 74, 74, 99, 99, 149, 149, 199, 199, 199];
const NEW_FP2D_SOLO = [149, 149, 174, 174, 199, 199, 249, 249, 299, 299, 299];
// The 3D floor plan add-on: quoted, never priced, at 10,000 sq ft and up.
const NEW_FP3D = [109, 109, 109, 109, 179, 179, 249, 249, 319, 319, null];

const NEW_AMBIENT_DUR = [60, 75, 90, 105, 120, 135, 150, 165, 180, 195, 225];
const NEW_FLASH_DUR = [120, 150, 180, 210, 240, 270, 300, 330, 360, 390, 450];
const NEW_LV_DUR = [120, 135, 150, 165, 180, 195, 210, 225, 240, 255, 285];
const NEW_PREMIUM_REEL_DUR = [45, 55, 65, 75, 85, 95, 105, 115, 125, 135, 165];
const NEW_REAL_TIME_DUR = [60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 180];
const NEW_FP2D_DUR = [30, 30, 35, 35, 40, 40, 40, 45, 45, 45, 60];
const NEW_MATTERPORT_DUR = [60, 60, 65, 70, 75, 80, 85, 90, 95, 100, 120];

// Option sets on the Video & Reels tab never cross: Listing Video options hide
// when only reels are picked, reel options when only listing cuts are. The
// Listing Video + Premium Reel pair takes both.
const NEW_REEL_ONLY = ['video_reel', 'reel_dynamic', 'reel_story', 'reel_simple'];
const NEW_LISTING_ONLY = ['video_mls', 'video_film', 'reel_simple'];
// Add-ons drawn on a 2D plan; hidden unless one is picked.
const NEW_NO_2D_PLAN = ['matterport', 'matterport_fp', 'zillow'];
// Products with no aerial stills to draw on.
const NEW_NO_AERIALS = ['photo_ie', 'photo_flash', 'photo_tw', 'aerial_lot_video'];

// An Old item the website has no counterpart for, carried into New unchanged.
const keepOldProduct = (id) => findProduct(id).product;
const keepOldAddon = (id) => SVC_DB.addons.find((a) => a.id === id);

/*
 * The New list, in SVC_DB's shape so the picker and the line rebuild drive it
 * the same way. Extra fields, read only by rgGetPrice / rgAddonPrice:
 * - `tiers` has 11 entries (10,000+ is its own tier) and prices by size band.
 * - `soloTiers`: charged instead when the quote has no shoot (`shoot: true`).
 * - `plusTiers`: a second website product sold as part of this one, priced the
 *   same way and added; `plus`: a flat option added after the size step.
 * - `flatWith`: a lower flat price when one of `ids` is also on the quote.
 * - `group`: a divider heading in the picker.
 * - Add-ons: `sizePrices` (by tier, null = quoted), `maxTier`, `requiresAddon`,
 *   `discountWith` ({ addonId, productIds, price }: the lower price when that
 *   add-on bills or one of those products is on the quote), `unit` (the qty
 *   badge).
 */
export const SVC_DB_NEW = {
  categories: [
    {
      id: 'photo', num: 1, name: 'Photos', color: '#06B3E8', icon: '📷',
      desc: 'From the ground, the air, or both. Sized for MLS, full resolution for print.',
      products: [
        { id: 'photo_ie', name: 'Ambient Photos', desc: 'Interior and exterior ground photos, natural light, HDR blended. 25 to 35 photos.',
          shoot: true, tiers: NEW_AMBIENT, dur: NEW_AMBIENT_DUR },
        { id: 'photo_iea', name: 'Ambient Photos + Aerial Photos', desc: 'Ambient Photos plus drone stills of the home and lot.',
          shoot: true, tiers: NEW_AMBIENT, plus: 150, dur: NEW_AMBIENT_DUR },
        { id: 'photo_flash', name: 'Flash Photos', desc: 'Every room lit with flash: punchier color, windows you can see through. 25 to 35 photos.',
          shoot: true, tiers: NEW_FLASH, dur: NEW_FLASH_DUR },
        { id: 'photo_flash_a', name: 'Flash Photos + Aerial Photos', desc: 'Flash Photos plus drone stills of the home and lot.',
          shoot: true, tiers: NEW_FLASH, plus: 150, dur: NEW_FLASH_DUR },
        { id: 'photo_tw', name: 'True Twilight', desc: 'A real on-location dusk session: real sky, real window glow. Not a sky swap.',
          shoot: true, flat: 299, dur: 60 },
        { ...keepOldProduct('photo_twa'), shoot: true },
      ],
    },
    {
      id: 'video', num: 2, name: 'Listing Video & Reels', tab: 'Video & Reels', color: '#C94A2A', icon: '🎬',
      desc: 'Listing cuts shot and edited like a film, and reels shot vertical for social.',
      products: [
        { id: 'video_mls', group: 'Listing video', name: 'Listing Video', desc: 'About 1 minute. Walkthrough and drone, color-graded, licensed music. MLS-ready.',
          shoot: true, tiers: NEW_LV, dur: NEW_LV_DUR },
        { id: 'video_film', name: 'Listing Film', desc: 'About 2 minutes, a mix of wide and tight shots, so more of the home makes the cut.',
          shoot: true, tiers: [1049, 1149, 1249, 1349, 1449, 1549, 1649, 1749, 1849, 1949, 2199], dur: NEW_LV_DUR },
        { id: 'video_combo', name: 'Listing Video + Premium Reel', desc: 'The Listing Video and a Premium Reel from the same visit.',
          shoot: true, tiers: NEW_LV, plusTiers: NEW_PREMIUM_REEL,
          dur: NEW_LV_DUR.map((d, i) => d + NEW_PREMIUM_REEL_DUR[i]) },
        { id: 'video_reel', group: 'Reels', name: 'Premium Reel', desc: '45 to 60s, shot and framed vertical. Elegant, slower cuts, trending audio.',
          shoot: true, tiers: NEW_PREMIUM_REEL, dur: NEW_PREMIUM_REEL_DUR },
        { id: 'reel_dynamic', name: 'Dynamic Reel', desc: '45 to 60s, speed-ramped: fast through the plain, slow on the detail. Speed ramps included.',
          shoot: true, tiers: NEW_PREMIUM_REEL.map((p) => p + NEW_REEL_SPEED_RAMPS),
          dur: [60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 180] },
        { id: 'reel_story', name: 'Story Reel', desc: 'A directed 60 to 90s cut: the agent on camera telling the story of the home, planned and scripted.',
          shoot: true, tiers: NEW_LV.map((p) => p + 300),
          dur: [75, 85, 95, 105, 115, 125, 135, 145, 155, 165, 195] },
        // Not a shoot of its own: it rides along with one, and doesn't count as
        // the 2D Floor Plan's shoot.
        { id: 'reel_simple', name: 'Simple Reel', desc: 'One vertical cut of wide shots, for the feed. Rides along with another shoot.',
          tiers: [299, 299, 349, 349, 399, 399, 449, 449, 499, 499, 599], wholeSteps: true, dur: 15 },
      ],
    },
    {
      id: 'drone', num: 3, name: 'RotoTour', tab: 'RotoTour', color: '#2E7D9B', icon: '🚁',
      desc: 'One continuous FPV drone flight, sky to inside.',
      products: [
        { id: 'drone_mls', name: 'Real Time Tour', desc: 'One continuous take, nothing sped up. Horizontal, for the listing page.',
          shoot: true, tiers: NEW_LV, dur: NEW_REAL_TIME_DUR },
        // A vertical RotoTour is always paced, so the Old social-format flight
        // lands here.
        { id: 'drone_reel', name: 'Paced Tour', desc: 'The flight, edited: slowing through the details and moving quickly between them. Horizontal or vertical.',
          shoot: true, tiers: NEW_PACED_TOUR, dur: [75, 85, 95, 105, 115, 125, 135, 145, 155, 165, 195] },
        { id: 'drone_story', name: 'Story Tour', desc: 'Designed pacing plus 3D titles anchored in the scene, so the story reveals as you fly.',
          shoot: true, tiers: NEW_STORY_TOUR, dur: [105, 115, 125, 135, 145, 155, 165, 175, 185, 195, 225] },
        // The website's second cut: one flight delivered both ways, +$249.
        { id: 'drone_bundle', name: 'Real Time Tour, both formats', desc: 'The Real Time Tour from one flight, delivered horizontal and vertical.',
          shoot: true, tiers: NEW_LV, plus: 249, dur: NEW_REAL_TIME_DUR },
      ],
    },
    {
      id: 'floor', num: 4, name: 'Floor Plans & 3D Tours', color: '#7B5EA7', icon: '🗺',
      desc: 'Floor plans, 3D walk-throughs and Zillow Showcase.',
      products: [
        { id: 'fp_2d', name: '2D Floor Plan', desc: 'Measured on site, from a scan of the home.',
          tiers: NEW_FP2D, soloTiers: NEW_FP2D_SOLO, wholeSteps: true, dur: NEW_FP2D_DUR },
        { id: 'fp_2d3d', name: '2D + 3D Floor Plan', desc: 'The 2D Floor Plan plus a 3D floor plan, in color and furnished.',
          tiers: NEW_FP2D, soloTiers: NEW_FP2D_SOLO, wholeSteps: true, plusTiers: NEW_FP3D, plusWholeSteps: true, dur: NEW_FP2D_DUR },
        { id: 'matterport', name: 'Matterport', desc: 'Interactive 3D walk-through.',
          tiers: [399, 449, 499, 549, 599, 649, 699, 749, 799, 849, 999], dur: NEW_MATTERPORT_DUR },
        { id: 'matterport_fp', name: 'Matterport Tour and Floor Plan', desc: '3D walk-through plus a floor plan drawn from the same scan.',
          tiers: [535, 635, 735, 835, 935, 1035, 1135, 1235, 1335, 1435, 1599], dur: NEW_MATTERPORT_DUR },
        { id: 'zillow', name: 'Zillow Showcase', desc: 'Shot and delivered to Zillow’s Showcase spec: 3D Home tour, interactive floor plan, room-tagged photos.',
          tiers: [249, 299, 349, 399, 449, 499, 549, 599, 649, 699, 849], dur: [30, 45, 60, 70, 80, 90, 100, 110, 120, 130, 150] },
      ],
    },
    {
      id: 'film', num: 5, name: 'Estate Film', tab: 'Estate Film', color: '#B8860B', icon: '🎞',
      desc: 'A 3 to 5 minute full production for estates and ranches, shot over multiple days with a full crew.',
      products: [
        { id: 'film_sig', name: 'Estate Film', desc: '3 to 5 minutes, multi-day, full crew. Every Listing Video option included.',
          shoot: true, tiers: [3499, 3649, 3799, 3949, 4099, 4249, 4399, 4549, 4699, 4849, 4999],
          dur: [240, 360, 360, 360, 360, 360, 360, 360, 360, 360, 360] },
      ],
    },
    {
      id: 'aerial', num: 6, name: 'Aerial & Land', tab: 'Aerial/Land', color: '#2E8B57', icon: '✈️',
      desc: 'Drone stills of the home, and land from the air. Flat rate, no sq ft needed.',
      products: [
        // $299 on its own; added to ground photos it is the $150 aerial option.
        { id: 'aerial_home', name: 'Aerial Photos', desc: 'Drone stills of the home, lot and surroundings. 5 to 10 photos, where airspace allows.',
          shoot: true, flat: 299, flatWith: { ids: ['photo_ie', 'photo_flash'], price: 150 }, dur: 30 },
        { id: 'aerial_lot_photo', name: 'Land Photography', desc: 'Aerial stills of the land and its surroundings.', flat: 299, dur: 30 },
        { id: 'aerial_lot_video', name: 'Land Film', desc: 'Cinematic aerial video of the land.', flat: 499, dur: 45 },
        { id: 'aerial_lot_bundle', name: 'Land Photo + Film', desc: 'Both, same shoot.', flat: 699, dur: 60, note: 'SAVE $99' },
      ],
    },
  ],

  addons: [
    // Photos
    { id: 'ao_vtwilight', name: 'Virtual twilight', desc: 'Your daytime photo, edited to look like dusk.', applies: ['photo'], perImage: true, price: 25 },
    { id: 'ao_vstaging', name: 'Virtual staging', desc: 'Empty rooms furnished in your photos, so buyers see the space lived in.', applies: ['photo'], excludeProducts: ['photo_tw', 'photo_twa'], perImage: true, price: 45 },
    { id: 'ao_proplines', name: 'Property lines', desc: 'Boundaries drawn on your aerial photos. Approximate, not a survey.', applies: ['photo', 'aerial'], excludeProducts: NEW_NO_AERIALS, perImage: true, price: 35 },
    { id: 'ao_beacons', name: 'Landmark callouts', desc: 'Beaches, schools, downtown: pinned and labeled on your aerial photos.', applies: ['photo', 'aerial'], excludeProducts: NEW_NO_AERIALS, perImage: true, price: 35 },
    { id: 'ao_ig_carousel', name: 'Instagram carousel', desc: 'Your photos cropped and ordered for one carousel post.', applies: ['photo'], price: 100 },
    // Listing Video options
    { id: 'ao_lv_fpv', name: 'RotoTour shots', desc: 'FPV cinema-drone moves, inside and out.', applies: ['video'], excludeProducts: NEW_REEL_ONLY, price: 200, durAdd: 30 },
    { id: 'ao_lv_agent', name: 'Agent on camera (Listing Video)', desc: 'Directed on-camera appearance with audio.', applies: ['video'], excludeProducts: NEW_REEL_ONLY, price: 250, durAdd: 30 },
    { id: 'ao_lv_ramps', name: 'Speed ramps (Listing Video)', desc: 'Fast-slow pacing, designed shot for shot.', applies: ['video'], excludeProducts: NEW_REEL_ONLY, price: 150, durAdd: 15 },
    { id: 'ao_timelapse', name: 'Timelapse shots (Listing Video)', desc: 'Clouds and shadows crossing the property, in seconds.', applies: ['video'], excludeProducts: NEW_REEL_ONLY, price: 150 },
    { id: 'ao_lv_teaser', name: 'Social media teaser cut (Listing Video)', desc: 'Your listing video, recut to a 10 to 15s vertical for the feed.', applies: ['video'], excludeProducts: NEW_REEL_ONLY, price: 150, durAdd: 30 },
    // Reel options. A Story Reel ships the agent and takes no captions or speed
    // ramps; a Dynamic Reel ships the speed ramps.
    { id: 'ao_reel_agent', name: 'Agent on camera (reel)', desc: 'Directed on-camera appearance with audio.', applies: ['video'], excludeProducts: [...NEW_LISTING_ONLY, 'reel_story'], price: 100, durAdd: 30 },
    { id: 'ao_reel_captions', name: 'Custom captions & scroll-stop hook', desc: 'Hand-crafted captions with an opening hook. Needs Agent on camera.', applies: ['video'], excludeProducts: [...NEW_LISTING_ONLY, 'reel_story'], requiresAddon: 'ao_reel_agent', price: 50 },
    { id: 'ao_reel_ramps', name: 'Speed ramps (reel)', desc: 'Fast-slow pacing, designed shot for shot.', applies: ['video'], excludeProducts: [...NEW_LISTING_ONLY, 'reel_dynamic', 'reel_story'], price: NEW_REEL_SPEED_RAMPS },
    // The Estate Film ships every Listing Video option, Timelapse included, so it
    // brings the reel's down the same way, as the website does.
    { id: 'ao_reel_timelapse', name: 'Timelapse shots (reel)', desc: 'Clouds and shadows crossing the property. $100 when the Listing Video or Estate Film already captures them.', applies: ['video'], excludeProducts: NEW_LISTING_ONLY, price: 150, discountWith: { addonId: 'ao_timelapse', productIds: ['film_sig'], price: 100 } },
    { id: 'ao_simple_extra', name: 'Additional Simple Reel', desc: 'More cuts from the same visit.', applies: ['video'], excludeProducts: ['video_mls', 'video_film', 'video_combo', 'video_reel', 'reel_dynamic', 'reel_story'], perImage: true, unit: 'each', price: 149, durAdd: 15 },
    // Video Length Upgrade and Luxury Upgrade + Video Length stay on the Old list
    // only: the Listing Film and Estate Film sell that work now, and a New quote
    // could otherwise charge for it twice (Cyprian, 2026-10-02).
    // RotoTour options
    { id: 'ao_tour_both', name: 'Both delivery formats', desc: 'Same flight, delivered horizontal and vertical.', applies: ['drone'], excludeProducts: ['drone_bundle'], price: 249 },
    { id: 'ao_tour_teaser', name: 'Social media teaser cut (RotoTour)', desc: 'Your RotoTour, recut to a 10 to 15s vertical for the feed.', applies: ['drone'], price: 150, durAdd: 30 },
    // 3D Graphics / Roto Tour (single and both) stays on the Old list only: the
    // Story Tour carries the 3D titles now (Cyprian, 2026-10-02).
    keepOldAddon('ao_d2n_single'),
    keepOldAddon('ao_d2n_both'),
    keepOldAddon('ao_branded_new'),
    keepOldAddon('ao_branded_ret'),
    // 2D Floor Plan extras (CubiCasa). Same-day stops at 3,999 sq ft.
    { id: 'ao_fp3d', name: '3D floor plan', desc: 'An extra 3D floor plan, in color and furnished. Quoted at 10,000 sq ft and up.', applies: ['floor'], excludeProducts: [...NEW_NO_2D_PLAN, 'fp_2d3d'], price: 109, sizePrices: NEW_FP3D },
    { id: 'ao_fpsameday', name: 'Same-day delivery', desc: 'Your 2D Floor Plan within 7 hours of the shoot.', applies: ['floor'], excludeProducts: NEW_NO_2D_PLAN, maxTier: 3, price: 50 },
    { id: 'ao_fpansi', name: 'ANSI square footage report', desc: 'Square footage measured to the ANSI standard, for listing and appraisal questions.', applies: ['floor'], excludeProducts: NEW_NO_2D_PLAN, price: 50 },
    { id: 'ao_fpadu', name: 'Separate ADU floor plan', desc: 'Your guest house or ADU on a plan of its own. On the main plan, it costs nothing extra.', applies: ['floor'], excludeProducts: NEW_NO_2D_PLAN, price: 50 },
    // Any shoot; never land on its own.
    { id: 'ao_website', name: 'Property website', desc: 'Single-listing site with custom domain.', applies: ['photo', 'video', 'drone', 'floor', 'film', 'aerial'], excludeProducts: ['aerial_lot_photo', 'aerial_lot_video', 'aerial_lot_bundle'], price: 150 },
  ],
};

/** The catalog a price version sells from. */
export function rgCatalog(version) {
  return rgNormalizeVersion(version) === RG_CURRENT_VERSION ? SVC_DB_NEW : SVC_DB;
}

/** Find a product in a version's catalog. Returns { category, product } or null. */
export function rgFindProduct(productId, version) {
  for (const cat of rgCatalog(version).categories) {
    const product = cat.products.find((p) => p.id === productId);
    if (product) return { category: cat, product };
  }
  return null;
}

/** Find an add-on in a version's catalog, or null. */
export function rgFindAddon(addonId, version) {
  return rgCatalog(version).addons.find((a) => a.id === addonId) ?? null;
}

/**
 * What the rest of the quote changes about a New-list price: whether it has a
 * shoot (the 2D Floor Plan's lower price), which products are on it, and which
 * add-ons bill (the reel's cheaper Timelapse beside the Listing Video's). Pass
 * rgBillableAddonIds, not the raw ticks: a ticked add-on whose product left
 * the quote no longer bills, so it can't discount anything either.
 */
export function rgPriceContext(selectedProductIds = [], billableAddonIds = []) {
  const selected = new Set(selectedProductIds);
  const hasShoot = [...selected].some(
    (id) => rgFindProduct(id, RG_CURRENT_VERSION)?.product.shoot
  );
  return { selected, hasShoot, addons: new Set(billableAddonIds) };
}

/** True for a product priced off a sq ft ladder (not flat, not film bands). */
export function rgIsTiered(product) {
  return !!product && product.flat === undefined && Array.isArray(product.tiers);
}

/**
 * Price and duration for a product at a square footage and price version.
 * `price` is null where the New list quotes rather than prices (the 3D floor
 * plan at 10,000+ sq ft). Old prices come straight from svcGetPrice.
 */
export function rgGetPrice(product, sqft = 0, version = RG_CURRENT_VERSION, ctx = null) {
  if (rgNormalizeVersion(version) !== RG_CURRENT_VERSION) return svcGetPrice(product, sqft);
  const t = rgTierIndex(sqft);
  const dur = Array.isArray(product.dur) ? product.dur[Math.min(t, product.dur.length - 1)] : product.dur;
  if (product.flat !== undefined) {
    const withIt = product.flatWith?.ids.some((id) => ctx?.selected?.has(id));
    return { price: withIt ? product.flatWith.price : product.flat, dur };
  }
  const up = rgUpperHalf(sqft);
  const ladder = product.soloTiers && !ctx?.hasShoot ? product.soloTiers : product.tiers;
  let price = rgStepPrice(ladder, t, up, product.wholeSteps);
  if (price != null && product.plusTiers) {
    const extra = rgStepPrice(product.plusTiers, t, up, product.plusWholeSteps);
    price = extra == null ? null : price + extra;
  }
  if (price != null && product.plus) price += product.plus;
  return { price, dur };
}

/** An add-on's price: by size where it has `sizePrices` (null = quoted). */
export function rgAddonPrice(addon, sqft = 0, ctx = null) {
  if (addon.sizePrices) {
    return addon.sizePrices[Math.min(rgTierIndex(sqft), addon.sizePrices.length - 1)] ?? null;
  }
  const dw = addon.discountWith;
  if (dw && (ctx?.addons?.has(dw.addonId) || dw.productIds?.some((id) => ctx?.selected?.has(id)))) {
    return dw.price;
  }
  return addon.price;
}

/** The size and requirement gates an add-on has beyond its category. */
export function rgAddonFits(addon, sqft = 0, selectedAddons = {}) {
  if (addon.maxTier != null && rgTierIndex(sqft) > addon.maxTier) return false;
  if (addon.requiresAddon && !selectedAddons?.[addon.requiresAddon]) return false;
  return true;
}

const RG_ONCE_PER_QUOTE = new Set(['ao_branded_new', 'ao_branded_ret']);

/**
 * The selected add-ons that actually bill: their category is on the quote, not
 * every picked product there excludes them, the once-per-quote ones once, and
 * the size and requirement gates pass. One rule for the line rebuild and the
 * picker's totals, so the two can't disagree.
 */
export function rgBillableAddonIds(selectedProducts = {}, selectedAddons = {}, sqft = 0, version = RG_CURRENT_VERSION) {
  const productIds = Object.keys(selectedProducts).filter((pid) => rgFindProduct(pid, version));
  const catIds = new Set(productIds.map((pid) => rgFindProduct(pid, version).category.id));
  const seenOnce = new Set();
  const out = new Set();
  for (const addonId of Object.keys(selectedAddons)) {
    const addon = rgFindAddon(addonId, version);
    if (!addon) continue;
    if (!addon.applies.some((c) => catIds.has(c))) continue;
    if (addon.excludeProducts?.length) {
      const relevant = productIds.filter((pid) => addon.applies.includes(rgFindProduct(pid, version).category.id));
      if (relevant.length > 0 && relevant.every((pid) => addon.excludeProducts.includes(pid))) continue;
    }
    if (!rgAddonFits(addon, sqft, selectedAddons)) continue;
    if (RG_ONCE_PER_QUOTE.has(addonId)) {
      if (seenOnce.has(addonId)) continue;
      seenOnce.add(addonId);
    }
    out.add(addonId);
  }
  return out;
}
